import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export default function MyTeam() {
  const { profile } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoach, setSelectedCoach] = useState(null);

  useEffect(() => {
    if (profile?.id) fetchMyCoaches();
  }, [profile]);

  const fetchMyCoaches = async () => {
    setLoading(true);
    try {
      // Get coaches assigned to this senior coach as mentor
      const { data: coachProfiles } = await supabase
        .from('coach_profiles')
        .select('*, user:user_id(id, full_name, email, created_at)')
        .eq('mentor_id', profile.id);

      if (!coachProfiles || coachProfiles.length === 0) {
        setCoaches([]);
        setLoading(false);
        return;
      }

      // Get revenue for each coach
      const coachIds = coachProfiles.map(cp => cp.user_id);
      const { data: transactions } = await supabase
        .from('transactions')
        .select('coach_id, gross_amount, mentor_amount, created_at')
        .in('coach_id', coachIds)
        .eq('status', 'completed');

      // Get active subscriptions count per coach
      const { data: subscriptions } = await supabase
        .from('subscriptions')
        .select('coach_id')
        .in('coach_id', coachIds)
        .eq('status', 'active');

      // Merge data
      const coachesWithStats = coachProfiles.map(cp => {
        const coachTxns = (transactions || []).filter(t => t.coach_id === cp.user_id);
        const totalRevenue = coachTxns.reduce((s, t) => s + Number(t.gross_amount || 0), 0);
        const myEarnings = coachTxns.reduce((s, t) => s + Number(t.mentor_amount || 0), 0);
        const activeSubs = (subscriptions || []).filter(s => s.coach_id === cp.user_id).length;

        return {
          ...cp.user,
          coachProfile: cp,
          totalRevenue,
          myEarnings,
          activeSubs,
          mentorPercentage: cp.mentor_percentage || 0
        };
      });

      setCoaches(coachesWithStats);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2 })}`;

  const totalMyEarnings = coaches.reduce((s, c) => s + c.myEarnings, 0);
  const totalCoachRevenue = coaches.reduce((s, c) => s + c.totalRevenue, 0);

  if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 32 }}>Loading...</div>;

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>My Team</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          {coaches.length} coach{coaches.length !== 1 ? 'es' : ''} assigned to you
        </p>
      </div>

      {/* Summary stats */}
      <div className="stat-grid" style={{ marginBottom: 32 }}>
        <div className="stat-card">
          <div className="stat-label">◉ My Coaches</div>
          <div className="stat-value">{coaches.length}</div>
          <div className="stat-sub">assigned to you</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">◇ Total Coach Revenue</div>
          <div className="stat-value">{fmt(totalCoachRevenue)}</div>
          <div className="stat-sub">across all your coaches</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">✦ My Earnings</div>
          <div className="stat-value">{fmt(totalMyEarnings)}</div>
          <div className="stat-sub">your mentor commission</div>
        </div>
      </div>

      {coaches.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 0',
          color: 'var(--text-muted)', border: '1px dashed var(--border)', borderRadius: 12
        }}>
          No coaches assigned to you yet. Contact your partner to get coaches assigned.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Coach</th>
                <th>Active Subscriptions</th>
                <th>Their Total Revenue</th>
                <th>My Earnings</th>
                <th>My Commission %</th>
                <th>Since</th>
              </tr>
            </thead>
            <tbody>
              {coaches.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{c.full_name || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.email}</div>
                  </td>
                  <td>
                    <span style={{
                      background: c.activeSubs > 0 ? 'rgba(76,175,130,0.12)' : 'rgba(90,85,80,0.2)',
                      color: c.activeSubs > 0 ? 'var(--success)' : 'var(--text-muted)',
                      padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500
                    }}>
                      {c.activeSubs} active
                    </span>
                  </td>
                  <td style={{ fontWeight: 500 }}>{fmt(c.totalRevenue)}</td>
                  <td style={{ color: 'var(--success)', fontWeight: 600 }}>{fmt(c.myEarnings)}</td>
                  <td style={{ color: 'var(--accent)' }}>{c.mentorPercentage}%</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {new Date(c.created_at).toLocaleDateString('en-CA')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
